import { useEffect, useState, useRef } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { Upload, FileText, Trash2, RefreshCw, Plus, BookOpen } from "lucide-react";

const inputCls =
  "w-full bg-white border border-[#E9EDEF] focus:border-[#25D366] focus:ring-1 focus:ring-[#25D366] text-[#111B21] rounded-lg px-3 py-2 text-sm placeholder:text-[#8696A0] focus:outline-none transition-colors";

function statusBadge(s) {
  const map = {
    indexed: "bg-green-500/10 text-green-600 border-green-500/20",
    processing: "bg-amber-500/10 text-amber-500 border-amber-500/20",
    failed: "bg-red-500/10 text-red-600 border-red-500/20",
  };
  return map[s] || map.processing;
}

export default function Knowledge() {
  const [docs, setDocs] = useState([]);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/knowledge").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/knowledge/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded & indexed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const addManual = async () => {
    if (!title.trim() || !content.trim()) { toast.error("Title and content required"); return; }
    setBusy(true);
    try {
      await api.post("/knowledge/manual", { title, content });
      toast.success("Knowledge added & indexed");
      setTitle(""); setContent(""); load();
    } catch (e) { toast.error("Failed to add"); }
    finally { setBusy(false); }
  };

  const del = async (id) => { await api.delete(`/knowledge/${id}`); load(); toast.success("Deleted"); };
  const reindex = async (id) => {
    try { await api.post(`/knowledge/${id}/reindex`); toast.success("Re-indexed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Re-index failed"); }
  };

  return (
    <>
      <PageHeader
        eyebrow="AI Engine" title="Knowledge Base"
        actions={
          <>
            <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md" onChange={upload} className="hidden" data-testid="knowledge-file-input" />
            <button data-testid="knowledge-upload-button" onClick={() => fileRef.current?.click()} disabled={busy}
              className="flex items-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-white rounded-lg font-medium px-4 py-2 text-sm transition-colors disabled:opacity-50">
              <Upload className="h-4 w-4" /> Upload PDF / DOCX / TXT
            </button>
          </>
        }
      />
      <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-[#E9EDEF] bg-white rounded-lg">
          <div className="px-5 py-4 border-b border-[#E9EDEF]">
            <h3 className="font-heading text-sm font-medium text-[#111B21]">Documents</h3>
          </div>
          <div className="divide-y divide-[#E9EDEF]">
            {docs.length === 0 && (
              <p className="px-5 py-10 text-center text-sm text-[#8696A0]">
                <BookOpen className="h-5 w-5 mx-auto mb-2 text-[#54656F]" />No knowledge yet. Upload files or add manual info.
              </p>
            )}
            {docs.map((d) => (
              <div key={d.document_id} className="flex items-center gap-3 px-5 py-3">
                <FileText className="h-4 w-4 text-[#8696A0]" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-[#111B21] truncate">{d.title}</p>
                  <p className="text-xs text-[#8696A0] font-mono">{d.chunks} chunks · {d.type}</p>
                </div>
                <span className={`font-mono text-[10px] px-2 py-0.5 rounded-lg border ${statusBadge(d.status)}`}>{d.status}</span>
                <button onClick={() => reindex(d.document_id)} className="text-[#8696A0] hover:text-[#075E54] transition-colors" data-testid={`reindex-${d.document_id}`}>
                  <RefreshCw className="h-4 w-4" />
                </button>
                <button onClick={() => del(d.document_id)} className="text-[#8696A0] hover:text-red-600 transition-colors" data-testid={`delete-${d.document_id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-[#E9EDEF] bg-white rounded-lg p-5 space-y-3 h-fit">
          <h3 className="font-heading text-sm font-medium text-[#111B21]">Add Manual Information</h3>
          <input data-testid="manual-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (e.g. Mixing FAQ)" className={inputCls} />
          <textarea data-testid="manual-content" rows={8} value={content} onChange={(e) => setContent(e.target.value)}
            placeholder="Paste MuviArts info: services, process, policies, FAQs…" className={inputCls} />
          <button data-testid="manual-add-button" onClick={addManual} disabled={busy}
            className="w-full flex items-center justify-center gap-2 bg-[#25D366] text-[#111B21] hover:bg-[#20BD5A] rounded-lg font-medium px-4 py-2 text-sm transition-colors disabled:opacity-50">
            <Plus className="h-4 w-4" /> Add to Knowledge Base
          </button>
        </div>
      </div>
    </>
  );
}
